import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'
import { timestampMs } from '../utils/timezone.js'
import type { HandoffEvent } from '../modules/chat-shared/timeline.js'
import { playWhatsAppMessageSound } from '../utils/whatsappNotificationSound.js'
import { showBrowserNotification } from '../utils/browserNotifications.js'

export interface WhatsAppChat {
  id: string
  wa_id: string
  profile_name: string | null
  display_name: string | null
  phone_number_id: string
  waba_id: string | null
  client_id: string | null
  assigned_agent_id: string | null
  assigned_at: string | null
  ai_assigned?: boolean
  ai_agent_name?: string | null
  client_name: string | null
  client_email: string | null
  status: string
  last_message_text: string | null
  last_message_at: string
  last_message_direction: string
  last_inbound_at: string | null
  unread_count: number
  is_read: boolean
  is_archived: boolean
  created_at: string
  updated_at: string
}

export interface WhatsAppChatMessage {
  id: string
  chat_id: string
  wa_id: string
  direction: 'inbound' | 'outbound'
  message_type: string
  content_text: string | null
  media_id: string | null
  media_mime_type: string | null
  media_filename: string | null
  template_name: string | null
  external_id: string | null
  status: 'received' | 'sent' | 'delivered' | 'read' | 'failed'
  error_code: string | null
  error_message: string | null
  conversation_id: string | null
  agent_user_id: string | null
  agent_name: string | null
  sent_at: string
}

export interface WhatsAppCall {
  id: string
  call_id: string
  chat_id: string | null
  wa_id: string
  direction: 'inbound' | 'outbound'
  status: string
  started_at: string
  connected_at: string | null
  ended_at: string | null
  duration_seconds: number | null
  terminate_reason: string | null
}

export interface WhatsAppConversationRef {
  id: string
  reference_id: string
  status: string
  subject: string | null
  is_archived: boolean
}

/**
 * A message the agent has sent but the server hasn't confirmed yet. It renders
 * immediately with a spinner so the thread never looks like it swallowed input,
 * and is dropped once the real row comes back from the server.
 */
export interface PendingWhatsAppMessage {
  localId: string
  content_text: string | null
  message_type: string
  media_filename?: string
  agent_name?: string | null
  status: 'sending' | 'failed'
  sent_at: string
}

export interface WhatsAppTemplate {
  id: string
  meta_template_name: string
  language_code: string
  category: string
  body_text: string
  variable_count: number
  header_type: string | null
  header_content: string | null
  footer_content: string | null
}

export interface WhatsAppAccountStatus {
  connected: boolean
  reason?: string
  wabaId?: string
  phoneNumberId?: string
  displayName?: string
}

/** The name to show for a thread, best-known first. */
export function chatDisplayName(chat: Pick<WhatsAppChat, 'display_name' | 'client_name' | 'profile_name' | 'wa_id'>): string {
  return chat.display_name || chat.client_name || chat.profile_name || `+${chat.wa_id}`
}

export function useWhatsAppChats(search?: string) {
  const [chats, setChats] = useState<WhatsAppChat[]>([])
  const [account, setAccount] = useState<WhatsAppAccountStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const bumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Track unread counts to detect new inbound messages */
  const prevUnreadMapRef = useRef<Map<string, number>>(new Map())

  const fetchChats = useCallback(async () => {
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : ''
      const res = await api.get<{ chats: WhatsAppChat[] }>(`/agent/whatsapp-chats${qs}`)
      const newChats = res.chats ?? []

      // Check for new inbound messages by comparing unread counts
      for (const chat of newChats) {
        const prevUnread = prevUnreadMapRef.current.get(chat.id) ?? 0
        if (chat.unread_count > prevUnread && chat.last_message_direction === 'inbound') {
          // New inbound message detected — play notification sound
          playWhatsAppMessageSound()
          const senderName = chat.display_name || chat.client_name || chat.profile_name || `+${chat.wa_id}`
          const preview = chat.last_message_text?.slice(0, 50) || 'New message'
          showBrowserNotification(`WhatsApp: ${senderName}`, preview)
          break // Only one notification per fetch cycle
        }
      }

      // Update the unread map for next comparison
      const newMap = new Map<string, number>()
      for (const chat of newChats) {
        newMap.set(chat.id, chat.unread_count)
      }
      prevUnreadMapRef.current = newMap

      setChats(newChats)
    } catch {
      // ignore — next bump retries
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    void fetchChats()
    void api
      .get<WhatsAppAccountStatus>('/agent/whatsapp-chats/account')
      .then(setAccount)
      .catch(() => setAccount({ connected: false, reason: 'lookup_failed' }))
    const socket = getSocket()
    const onBump = (payload: { channel?: string }) => {
      if (payload.channel !== 'whatsapp') return
      if (bumpTimerRef.current) clearTimeout(bumpTimerRef.current)
      bumpTimerRef.current = setTimeout(() => void fetchChats(), 400)
    }
    socket.on('chat:list:bump', onBump)
    return () => {
      if (bumpTimerRef.current) clearTimeout(bumpTimerRef.current)
      socket.off('chat:list:bump', onBump)
    }
  }, [fetchChats])

  const markRead = useCallback(async (chatId: string) => {
    // Optimistic — the badge should clear the instant the agent opens the thread.
    setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, is_read: true, unread_count: 0 } : c)))
    try {
      await api.post(`/agent/whatsapp-chats/${chatId}/read`)
    } catch {
      // ignore
    }
  }, [])

  const startChat = useCallback(
    async (input: {
      phone: string
      name?: string
      text?: string
      template?: { name: string; languageCode: string; variables: string[] }
    }) => {
      const res = await api.post<{ chatId: string; waId: string }>('/agent/whatsapp-chats', input)
      await fetchChats()
      return res
    },
    [fetchChats],
  )

  return { chats, account, loading, fetchChats, markRead, startChat }
}

export function useWhatsAppChatMessages(chatId: string | null) {
  const [messages, setMessages] = useState<WhatsAppChatMessage[]>([])
  const [chat, setChat] = useState<WhatsAppChat | null>(null)
  const [calls, setCalls] = useState<WhatsAppCall[]>([])
  const [conversations, setConversations] = useState<WhatsAppConversationRef[]>([])
  const [handoffEvents, setHandoffEvents] = useState<HandoffEvent[]>([])
  const [pending, setPending] = useState<PendingWhatsAppMessage[]>([])
  const [sessionOpen, setSessionOpen] = useState(false)
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const beforeRef = useRef<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const messagesRef = useRef<WhatsAppChatMessage[]>(messages)
  messagesRef.current = messages

  const chatRef = useRef<WhatsAppChat | null>(chat)
  chatRef.current = chat

  const mergeMessages = useCallback((prev: WhatsAppChatMessage[], next: WhatsAppChatMessage[]) => {
    const byId = new Map<string, WhatsAppChatMessage>()
    for (const m of prev) byId.set(m.id, m)
    for (const m of next) byId.set(m.id, m)
    return Array.from(byId.values()).sort((a, b) => timestampMs(a.sent_at) - timestampMs(b.sent_at))
  }, [])

  const fetchMessages = useCallback(async () => {
    if (!chatId) return
    try {
      const res = await api.get<{
        chat: WhatsAppChat
        messages: WhatsAppChatMessage[]
        hasMore: boolean
        before?: string
        calls: WhatsAppCall[]
        conversations: WhatsAppConversationRef[]
        handoff_events?: HandoffEvent[]
        sessionOpen: boolean
        sessionExpiresAt: string | null
      }>(`/agent/whatsapp-chats/${chatId}?limit=20`)
      setChat(res.chat ?? null)
      chatRef.current = res.chat ?? null
      setMessages((prev) => mergeMessages(prev, res.messages ?? []))
      setHasMore(res.hasMore ?? false)
      if (messagesRef.current.length === 0) beforeRef.current = res.before ?? null
      setCalls(res.calls ?? [])
      setConversations(res.conversations ?? [])
      setHandoffEvents(res.handoff_events ?? [])
      setSessionOpen(res.sessionOpen ?? false)
      setSessionExpiresAt(res.sessionExpiresAt ?? null)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [chatId, mergeMessages])

  const loadMore = useCallback(async () => {
    if (!chatId || beforeRef.current === null || !hasMore || loadingMore) return
    setLoadingMore(true)
    try {
      const res = await api.get<{
        messages: WhatsAppChatMessage[]
        hasMore: boolean
        before?: string
      }>(`/agent/whatsapp-chats/${chatId}?before=${encodeURIComponent(beforeRef.current)}&limit=20`)
      setMessages((prev) => [...(res.messages ?? []), ...prev])
      setHasMore(res.hasMore ?? false)
      beforeRef.current = res.before ?? null
    } catch {
      // ignore
    } finally {
      setLoadingMore(false)
    }
  }, [chatId, hasMore, loadingMore])

  useEffect(() => {
    if (!chatId) {
      setMessages([])
      setChat(null)
      chatRef.current = null
      setCalls([])
      setConversations([])
      setHandoffEvents([])
      setPending([])
      return
    }
    setLoading(true)
    setLoadingMore(false)
    setHasMore(false)
    beforeRef.current = null
    void fetchMessages()

    const socket = getSocket()
    const onBump = (payload: { channel?: string; conversationId?: string }) => {
      if (payload.channel !== 'whatsapp') return
      const targetMatches =
        !payload.conversationId ||
        payload.conversationId === chatId ||
        (chatRef.current && (payload.conversationId === chatRef.current.wa_id || payload.conversationId === chatRef.current.id))
      if (targetMatches) {
        void fetchMessages()
      }
    }
    const onReconnect = () => void fetchMessages()

    socket.on('chat:list:bump', onBump)
    socket.on('connect', onReconnect)

    // Dynamic fallback: 15s when WebSocket is active, 4s if disconnected or reconnecting
    const setupTimer = () => {
      if (pollRef.current) clearInterval(pollRef.current)
      const intervalMs = socket.connected ? 15000 : 4000
      pollRef.current = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          void fetchMessages()
        }
      }, intervalMs)
    }
    setupTimer()
    socket.on('connect', setupTimer)
    socket.on('disconnect', setupTimer)

    return () => {
      socket.off('chat:list:bump', onBump)
      socket.off('connect', onReconnect)
      socket.off('connect', setupTimer)
      socket.off('disconnect', setupTimer)
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [chatId, fetchMessages])

  /**
   * Shows the message straight away, then swaps it for the server's copy.
   * A failed send stays on screen marked failed rather than vanishing, so the
   * agent knows the customer never got it.
   */
  const sendOptimistic = useCallback(
    async (
      draft: Omit<PendingWhatsAppMessage, 'localId' | 'status' | 'sent_at'>,
      send: () => Promise<unknown>,
    ) => {
      const localId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      setPending((prev) => [
        ...prev,
        { ...draft, localId, status: 'sending', sent_at: new Date().toISOString() },
      ])
      try {
        await send()
        await fetchMessages()
        setPending((prev) => prev.filter((p) => p.localId !== localId))
      } catch (err) {
        setPending((prev) =>
          prev.map((p) => (p.localId === localId ? { ...p, status: 'failed' } : p)),
        )
        throw err
      }
    },
    [fetchMessages],
  )

  const dismissPending = useCallback((localId: string) => {
    setPending((prev) => prev.filter((p) => p.localId !== localId))
  }, [])

  const sendMessage = useCallback(
    async (text: string) => {
      if (!chatId || !text.trim()) return
      await sendOptimistic({ content_text: text.trim(), message_type: 'text' }, () =>
        api.post(`/agent/whatsapp-chats/${chatId}/reply`, { text }),
      )
    },
    [chatId, sendOptimistic],
  )

  const sendTemplate = useCallback(
    async (input: { name: string; languageCode: string; variables: string[] }) => {
      if (!chatId) return
      const summary = input.variables.length
        ? `${input.name}: ${input.variables.join(', ')}`
        : input.name
      await sendOptimistic({ content_text: summary, message_type: 'template' }, () =>
        api.post(`/agent/whatsapp-chats/${chatId}/template`, input),
      )
    },
    [chatId, sendOptimistic],
  )

  const sendAttachment = useCallback(
    async (input: { attachmentId: string; filename: string; caption?: string }) => {
      if (!chatId) return
      await sendOptimistic(
        {
          content_text: input.caption ?? null,
          message_type: 'document',
          media_filename: input.filename,
        },
        () =>
          api.post(`/agent/whatsapp-chats/${chatId}/media`, {
            attachmentId: input.attachmentId,
            ...(input.caption ? { caption: input.caption } : {}),
          }),
      )
    },
    [chatId, sendOptimistic],
  )

  const saveContact = useCallback(
    async (input: { fullName: string; email?: string }) => {
      if (!chatId) return null
      const res = await api.post<{ client: { id: string; full_name: string }; created: boolean }>(
        `/agent/whatsapp-chats/${chatId}/save-contact`,
        input,
      )
      await fetchMessages()
      return res
    },
    [chatId, fetchMessages],
  )

  /** Omitting `messageIds` promotes every message not already on a conversation. */
  const createTicket = useCallback(
    async (messageIds?: string[], subject?: string) => {
      if (!chatId) return null
      const res = await api.post<{ conversationId: string; referenceId: string }>(
        `/agent/whatsapp-chats/${chatId}/create-ticket`,
        {
          ...(messageIds && messageIds.length > 0 ? { messageIds } : {}),
          ...(subject ? { subject } : {}),
        },
      )
      await fetchMessages()
      return res
    },
    [chatId, fetchMessages],
  )

  return {
    chat,
    messages,
    handoffEvents,
    calls,
    conversations,
    pending,
    loading,
    sessionOpen,
    sessionExpiresAt,
    loadingMore,
    hasMore,
    sendMessage,
    sendTemplate,
    sendAttachment,
    saveContact,
    createTicket,
    dismissPending,
    loadMore,
    fetchMessages,
  }
}

export function useWhatsAppTemplates() {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void api
      .get<{ templates: WhatsAppTemplate[] }>('/agent/whatsapp-chats/templates')
      .then((res) => {
        if (!cancelled) setTemplates(res.templates ?? [])
      })
      .catch(() => {
        if (!cancelled) setTemplates([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { templates, loading }
}
